import { db } from '../firebase'
import { 
  collection, 
  addDoc, 
  updateDoc,
  doc,
  getDocs,
  getDoc,
  query,
  serverTimestamp,
  where,
  orderBy,
  limit,
  increment 
} from 'firebase/firestore'
import PDFGenerator from './pdfGenerator'
import { businessProfileService } from '@/services/businessProfileService'
import { voucherService } from '@/services/voucherService'

export class InvoiceGenerator {
  static async getCustomerFinancialSnapshot(customerId) {
    if (!customerId || customerId === 'walk-in') return null

    try {
      const snap = await getDoc(doc(db, 'customers', customerId))
      if (!snap.exists()) return null
      const data = snap.data()
      return {
        loyaltyPoints: Number(data.loyaltyPoints || 0),
        voucherBalance: Number(data.voucherBalance || 0),
        hasActiveSubscription: Boolean(data.hasActiveSubscription),
        customerCode: data.customerCode || '',
        phone: data.phone || '',
      }
    } catch (e) {
      console.warn('Failed to load customer snapshot for receipt:', e)
      return null
    }
  }

  static async getActiveSubscriptionSnapshot(customerId, branchId) {
    if (!customerId || customerId === 'walk-in') return null
    try {
      const constraints = [
        where('customerId', '==', customerId),
        where('isActive', '==', true),
      ]
      if (branchId) constraints.push(where('branchId', '==', branchId))

      const q = query(
        collection(db, 'subscriptions'),
        ...constraints,
        orderBy('expiryDate', 'desc'),
        limit(1)
      )

      const snap = await getDocs(q)
      if (snap.empty) return null
      const sub = snap.docs[0].data()

      const washesIncluded = sub.washesIncluded
      const washesUsed = Number(sub.washesUsed || 0)
      const remaining =
        typeof washesIncluded === 'number'
          ? Math.max(0, washesIncluded - washesUsed)
          : washesIncluded === 'unlimited'
            ? 'unlimited'
            : null

      const expiry =
        sub.expiryDate?.toDate && typeof sub.expiryDate.toDate === 'function'
          ? sub.expiryDate.toDate().toISOString()
          : sub.expiryDate
            ? new Date(sub.expiryDate).toISOString()
            : null

      return {
        planType: sub.planType || '',
        washesIncluded,
        washesUsed,
        remaining,
        expiryDate: expiry,
      }
    } catch (e) {
      console.warn('Failed to load subscription snapshot for receipt:', e)
      return null
    }
  }

  static async generateInvoice(transactionData) {
    try {
      const [customerSnapshot, subscriptionSnapshot] = await Promise.all([
        this.getCustomerFinancialSnapshot(transactionData.customerId),
        this.getActiveSubscriptionSnapshot(transactionData.customerId, transactionData.branchId),
      ])

      // Prepare invoice data
      const invoiceData = {
        id: `INV-${Date.now()}`,
        type: 'invoice',
        issueDate: new Date().toISOString(),
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days
        customerId: transactionData.customerId,
        customerName: transactionData.customerName,
        customerPhone: transactionData.customerPhone || customerSnapshot?.phone || '',
        customerCode: transactionData.customerCode || customerSnapshot?.customerCode || '',
        branchId: transactionData.branchId,
        staffId: transactionData.staffId,
        items: transactionData.items.map(item => ({
          id: item.id,
          description: item.name,
          category: item.category,
          quantity: item.quantity,
          unitPrice: item.price,
          total: item.quantity * item.price
        })),
        subtotal: transactionData.subtotal,
        discount: {
          percentage: transactionData.discountPercentage || 0,
          amount: transactionData.discountAmount || 0,
          reason: transactionData.discountReason || ''
        },
        vatRate: 15, // South African VAT rate
        vatAmount: transactionData.subtotal * 0.15,
        total: transactionData.total,
        paymentMethod: transactionData.paymentMethod,
        status: 'paid',
        metadata: {
          wasSessionId: transactionData.washSessionId || null,
          platform: 'CarWash POS',
          version: '1.0.0',
          location: transactionData.location || 'Main Branch',
          customerSnapshot,
          subscriptionSnapshot,
        }
      }

      // Save to Firestore
      const invoiceRef = await addDoc(collection(db, 'invoices'), {
        ...invoiceData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })

      invoiceData.firestoreId = invoiceRef.id

      // Voucher program: accumulate qualifying spend (exclude subscription packages)
      if (transactionData.customerId && transactionData.customerId !== 'walk-in') {
        const subscriptionTotal = (transactionData.items || [])
          .filter((item) => String(item.category || '').toLowerCase() === 'subscription')
          .reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1), 0)
        const qualifyingAmount = Math.max(0, Number(transactionData.total || 0) - subscriptionTotal)
        if (qualifyingAmount > 0) {
          try {
            await voucherService.applySpendAndIssueVouchers({
              customerId: transactionData.customerId,
              customerName: transactionData.customerName,
              branchId: transactionData.branchId,
              amount: qualifyingAmount,
              sourceRef: { type: 'invoice', id: invoiceData.id, firestoreId: invoiceRef.id },
            })
          } catch (e) {
            console.error('Voucher program update failed:', e)
          }
        }
      }

      // Track analytics
      await this.trackInvoiceAnalytics(invoiceData)

      return invoiceData
    } catch (error) {
      console.error('Error generating invoice:', error)
      throw new Error('Failed to generate invoice')
    }
  }

  static async generateCreditNote(originalInvoiceId, creditData) {
    try {
      const creditNoteData = {
        id: `CN-${Date.now()}`,
        type: 'credit_note',
        originalInvoiceId,
        issueDate: new Date().toISOString(),
        customerId: creditData.customerId,
        customerName: creditData.customerName,
        branchId: creditData.branchId,
        staffId: creditData.staffId,
        reason: creditData.reason,
        items: creditData.items.map(item => ({
          id: item.id,
          description: item.name,
          category: item.category,
          quantity: item.quantity,
          unitPrice: item.price,
          total: item.quantity * item.price
        })),
        subtotal: creditData.subtotal,
        vatRate: 15,
        vatAmount: creditData.subtotal * 0.15,
        total: creditData.total,
        status: 'issued',
        metadata: {
          platform: 'CarWash POS',
          version: '1.0.0',
          refundMethod: creditData.refundMethod || 'account_credit'
        }
      }

      // Save to Firestore
      const creditNoteRef = await addDoc(collection(db, 'credit_notes'), {
        ...creditNoteData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })

      creditNoteData.firestoreId = creditNoteRef.id

      // Track analytics
      await this.trackCreditNoteAnalytics(creditNoteData)

      return creditNoteData
    } catch (error) {
      console.error('Error generating credit note:', error)
      throw new Error('Failed to generate credit note')
    }
  }

  static async resolveBranchForSlip(branchId) {
    if (!branchId) return null
    try {
      const { BranchService } = await import('@/services/branchService')
      return await BranchService.getBranchById(branchId)
    } catch (e) {
      console.warn('Could not load branch for slip:', e)
      return null
    }
  }

  static async enrichInvoiceForSlip(invoiceData) {
    const data = { ...invoiceData }
    if (!data.customerCode && data.customerId && data.customerId !== 'walk-in') {
      try {
        const snap = await getDoc(doc(db, 'customers', data.customerId))
        if (snap.exists()) {
          data.customerCode = snap.data().customerCode || ''
          data.customerPhone = data.customerPhone || snap.data().phone || ''
        }
      } catch (e) {
        console.warn('Could not load customer code for slip:', e)
      }
    }
    return data
  }

  static async generateThermalReceipt(invoiceData) {
    try {
      const [businessProfile, branch, enriched] = await Promise.all([
        businessProfileService.get(),
        this.resolveBranchForSlip(invoiceData.branchId),
        this.enrichInvoiceForSlip(invoiceData),
      ])
      const blob = await PDFGenerator.generateThermalReceipt(enriched, businessProfile, branch)
      
      // Track printing analytics
      await this.trackPrintAnalytics(invoiceData.id, 'thermal_receipt')
      
      return blob
    } catch (error) {
      console.error('Error generating thermal receipt:', error)
      throw new Error('Failed to generate thermal receipt')
    }
  }

  static async generateA4Invoice(invoiceData) {
    try {
      const [businessProfile, branch, enriched] = await Promise.all([
        businessProfileService.get(),
        this.resolveBranchForSlip(invoiceData.branchId),
        this.enrichInvoiceForSlip(invoiceData),
      ])
      const blob = await PDFGenerator.generateA4Invoice(enriched, businessProfile, branch)
      
      // Track printing analytics
      await this.trackPrintAnalytics(invoiceData.id, 'a4_invoice')
      
      return blob
    } catch (error) {
      console.error('Error generating A4 invoice:', error)
      throw new Error('Failed to generate A4 invoice')
    }
  }

  static async printThermalReceipt(invoiceData) {
    try {
      const blob = await this.generateThermalReceipt(invoiceData)
      await PDFGenerator.printThermalReceipt(blob)
      
      // Track print action
      await this.trackPrintAction(invoiceData.id, 'thermal_print')
      
      return true
    } catch (error) {
      console.error('Error printing thermal receipt:', error)
      throw new Error('Failed to print thermal receipt')
    }
  }

  static async downloadA4Invoice(invoiceData) {
    try {
      const blob = await this.generateA4Invoice(invoiceData)
      const filename = `invoice-${invoiceData.id}.pdf`
      PDFGenerator.downloadPDF(blob, filename)
      
      // Track download action
      await this.trackPrintAction(invoiceData.id, 'a4_download')
      
      return true
    } catch (error) {
      console.error('Error downloading A4 invoice:', error)
      throw new Error('Failed to download A4 invoice')
    }
  }

  static async trackInvoiceAnalytics(invoiceData) {
    try {
      const analyticsData = {
        event: 'invoice_generated',
        timestamp: serverTimestamp(),
        branchId: invoiceData.branchId,
        staffId: invoiceData.staffId,
        customerId: invoiceData.customerId,
        amount: invoiceData.total,
        itemCount: invoiceData.items.length,
        paymentMethod: invoiceData.paymentMethod,
        hasDiscount: invoiceData.discount.amount > 0,
        metadata: {
          invoiceId: invoiceData.id,
          categories: [...new Set(invoiceData.items.map(item => item.category))],
          platform: 'POS'
        }
      }

      // Save analytics event
      await addDoc(collection(db, 'analytics'), analyticsData)

      // Update daily stats
      const today = new Date().toISOString().split('T')[0]
      const statsRef = doc(db, 'dailyStats', `${invoiceData.branchId}-${today}`)
      
      await updateDoc(statsRef, {
        totalSales: increment(invoiceData.total),
        invoiceCount: increment(1),
        lastUpdated: serverTimestamp()
      }).catch(() => {
        // If document doesn't exist, create it
        addDoc(collection(db, 'dailyStats'), {
          id: `${invoiceData.branchId}-${today}`,
          branchId: invoiceData.branchId,
          date: today,
          totalSales: invoiceData.total,
          invoiceCount: 1,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp()
        })
      })

    } catch (error) {
      console.error('Error tracking invoice analytics:', error)
      // Don't throw - analytics failure shouldn't break the invoice flow
    }
  }

  static async trackCreditNoteAnalytics(creditNoteData) {
    try {
      const analyticsData = {
        event: 'credit_note_generated',
        timestamp: serverTimestamp(),
        branchId: creditNoteData.branchId,
        staffId: creditNoteData.staffId,
        customerId: creditNoteData.customerId,
        amount: creditNoteData.total,
        reason: creditNoteData.reason,
        metadata: {
          creditNoteId: creditNoteData.id,
          originalInvoiceId: creditNoteData.originalInvoiceId,
          platform: 'POS'
        }
      }

      await addDoc(collection(db, 'analytics'), analyticsData)

      // Update daily stats
      const today = new Date().toISOString().split('T')[0]
      const statsRef = doc(db, 'dailyStats', `${creditNoteData.branchId}-${today}`)
      
      await updateDoc(statsRef, {
        totalRefunds: increment(creditNoteData.total),
        creditNoteCount: increment(1),
        lastUpdated: serverTimestamp()
      }).catch(() => {
        // Create new daily stats document if it doesn't exist
        addDoc(collection(db, 'dailyStats'), {
          id: `${creditNoteData.branchId}-${today}`,
          branchId: creditNoteData.branchId,
          date: today,
          totalRefunds: creditNoteData.total,
          creditNoteCount: 1,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp()
        })
      })

    } catch (error) {
      console.error('Error tracking credit note analytics:', error)
    }
  }

  static async trackPrintAnalytics(documentId, printType) {
    try {
      const analyticsData = {
        event: 'document_generated',
        documentId,
        printType,
        timestamp: serverTimestamp(),
        metadata: {
          platform: 'POS',
          userAgent: navigator.userAgent
        }
      }

      await addDoc(collection(db, 'analytics'), analyticsData)
    } catch (error) {
      console.error('Error tracking print analytics:', error)
    }
  }

  static async trackPrintAction(documentId, actionType) {
    try {
      const analyticsData = {
        event: 'document_action',
        documentId,
        actionType,
        timestamp: serverTimestamp(),
        metadata: {
          platform: 'POS',
          userAgent: navigator.userAgent
        }
      }

      await addDoc(collection(db, 'analytics'), analyticsData)
    } catch (error) {
      console.error('Error tracking print action:', error)
    }
  }

  // Utility methods
  static formatCurrency(amount) {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2
    }).format(amount)
  }

  static calculateVAT(amount, rate = 15) {
    return amount * (rate / 100)
  }

  static generateInvoiceNumber() {
    const timestamp = Date.now()
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0')
    return `INV-${timestamp}-${random}`
  }

  static generateCreditNoteNumber() {
    const timestamp = Date.now()
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0')
    return `CN-${timestamp}-${random}`
  }
}

export default InvoiceGenerator 